import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, Typography, useMediaQuery, useTheme } from "@mui/material";
import { useContext, useEffect, useState } from "react";
import StyledOutlinedTextField from "../../styledComponents/OutlinedTextField";
import { useAxios } from "../../utils/useAxios";
import { UserContext } from "../../utils/UserContext";
import { useTranslation } from "react-i18next";

/**
 * MobilBoxDialog
 *
 * This component displays a dialog prompting the (robot) user to set their
 * voicemail/mobilbox password. It is shown automatically when the current
 * `user` object indicates `roboter === true` and `mobilboxPassword` is
 * missing/undefined. The component renders a compact mobile dialog for small
 * screens and a larger desktop dialog otherwise.
 *
 * Behavior:
 * - On mount (and when `user` changes) the component checks the user state
 *   and opens the dialog if the mobilbox PIN is not set.
 * - The dialog allows the user to enter a mobilbox PIN and saves it via the
 *   `savePassword` function which calls the backend endpoint
 *   `/mobilboxpassword/{password}`. On success the user's `mobilboxPassword`
 *   is updated and the dialog closes.
 */
const MobilBoxDialog = () => {
    const theme = useTheme();
    const belowsm = useMediaQuery(theme.breakpoints.down("sm"));

    const [open, setOpen] = useState(false);

    const { user, setUser } = useContext(UserContext);

    const axiosInstance = useAxios();

    const { t } = useTranslation();

    useEffect(() => {
        if (user.userID !== undefined) {
            if (user.roboter === true) {
                if (user.mobilboxPassword === undefined)  {
                    setOpen(true);
                } else {
                    setOpen(false);
                }
            } else {
                setOpen(false);
            }  
            
        }
    }, [user, belowsm])

    const savePassword = (password) => {
        console.log("saving password...");
        axiosInstance.current({
            url: `/mobilboxpassword/${password}`,
            method: "POST"
        }).then(res => {
            if (res.status === 200) {
                setUser({ ...user, mobilboxPassword: res.data })
                setOpen(false);
                console.log("password saved");
            }
        }).catch(err => {
            console.error(err.response);
        })
    }

    const MobileDialog = () => {

        const [password, setPassword] = useState("");

        return (
            <Dialog
                open={open}
                fullWidth
                maxWidth="xs"
                sx={{
                    "& .MuiDialog-paper": {
                        backgroundImage: "none"
                    }
                }}
            >
                <DialogTitle sx={{ borderBottom: 1, borderColor: "divider", display: "flex", justifyContent: "center" }}>
                    <Typography color="text.primary">{t("voicemail-password-not-set")}</Typography>
                </DialogTitle>
                <DialogContent>
                    <Grid container direction="row" sx={{ display: "flex", justifyContent: "center" }}>
                        <StyledOutlinedTextField
                            variant="outlined"
                            label={t("voicemail-password")}
                            type="text"
                            value={password}
                            onChange={(event) => { setPassword((event.target.value || '').replace(/\s+/g, '').replace(/\D/g, '')) }}
                            InputLabelProps={{ shrink: true }}
                            inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                            sx={{ mt: 3, width: "90%" }}
                        />
                        <Typography color="text.secondary" sx={{ display: "flex", justifyContent: "center", width: "90%", mt: 1, fontSize: 12 }}>{t("missing-voicemail-password-info")}</Typography>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ borderTop: 1, borderColor: "divider" }}>
                    <Button onClick={() => { savePassword(password) }} disabled={password === ""} sx={{ color: "text.secondary", textTransform: "none", borderRadius: "16px", "&:hover": { color: "text.primary" }, "&:disabled": { color: "action.disabled" } }}>{t("save")}</Button>
                </DialogActions>
            </Dialog>
        )
    };

    const DesktopDialog = () => {

        const [password, setPassword] = useState("");

        return (
            <Dialog
                open={open}
                fullWidth
                maxWidth="sm"
                sx={{
                    "& .MuiDialog-paper": {
                        backgroundImage: "none"
                    }
                }}
            >
                <DialogTitle sx={{ borderBottom: 1, borderColor: "divider", display: "flex", justifyContent: "center" }}>
                    <Typography color="text.primary">{t("voicemail-password-not-set")}</Typography>
                </DialogTitle>
                <DialogContent>
                    <Grid container direction="row">
                        <Grid item xs={12} sx={{ display: "flex", justifyContent: "center" }}>
                            <StyledOutlinedTextField
                                variant="outlined"
                                label={t("voicemail-password")}
                                type="text"
                                value={password}
                                onChange={(event) => { setPassword((event.target.value || '').replace(/\s+/g, '').replace(/\D/g, '')) }}
                                InputLabelProps={{ shrink: true }}
                                inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                                sx={{ display: "flex", justifyContent: "center", width: "80%", mt: 3 }}
                            />
                        </Grid>
                        <Grid item xs={12} sx={{ display: "flex", justifyContent: "center" }}>
                            <Typography color="text.secondary" sx={{ display: "flex", justifyContent: "center", width: "80%", mt: 1, fontSize: 12 }}>{t("missing-voicemail-password-info")}</Typography>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ borderTop: 1, borderColor: "divider" }}>
                    <Button onClick={() => { savePassword(password) }} disabled={password === ""} sx={{ color: "text.secondary", textTransform: "none", borderRadius: "16px", "&:hover": { color: "text.primary" }, "&:disabled": { color: "action.disabled" } }}>{t("save")}</Button>
                </DialogActions>
            </Dialog>
        )
    }

    return belowsm ? <MobileDialog /> : <DesktopDialog />
};

export default MobilBoxDialog;